import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Paper,
  Accordion,
  AccordionSummary,
  Box,
  Typography,
  AccordionDetails,
  TextField,
  Stack,
  Chip,
  Theme,
  Button,
  Tooltip,
} from '@mui/material';
import { Draggable } from 'react-beautiful-dnd';

import { Effect } from '../../@types/Models';
import { KanbanColumn, KanbanItem, Objective } from '../../@types/State';
import { effects } from '../../Effects';

interface Colors {
  [key: string]: any;
}

const colors: Colors = {
  Gn: 'Lime',
  El: 'Orange',
  Ha: 'SteelBlue',
  Or: 'SeaGreen',
  Un: 'Yellow',
  Av: 'DodgerBlue',
  Dw: 'Khaki',
  Dr: 'Green',
  Fa: 'RoyalBlue',
  Da: 'Khaki',
  Hu: 'SteelBlue',
};

interface Props {
  item: KanbanItem;
  itemInd: number;
  column: KanbanColumn;
  colInd: number;
  theme: Theme;
  selected: KanbanItem | null;
  isWarPlan: boolean;
  handleCollapse: (
    item: KanbanItem
  ) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  handleItemChange: (e: any, colIndex: number, itemInd: number) => void;
  deleteOp: (op: string, selected: KanbanItem | null) => void;
  deleteObjective: (op: Objective) => void;
  toggleDrawer: () => void;
}

const Item: React.FC<Props> = ({
  item,
  itemInd,
  column,
  colInd,
  theme,
  selected,
  isWarPlan,
  handleCollapse,
  handleItemChange,
  deleteOp,
  deleteObjective,
  toggleDrawer,
}) => {
  const race = item.province.race ?? '?';
  return (
    <Draggable key={item.id} draggableId={item.id} index={itemInd}>
      {(provided, snapshot) => (
        <Paper
          sx={{
            margin: 0,
            marginTop: 1,
            border:
              selected === item
                ? `1px solid ${theme.palette.primary.main}`
                : 'none',
          }}
          style={{ ...provided.draggableProps.style }}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <Accordion
            disableGutters
            expanded={selected === item}
            onChange={handleCollapse(item)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ padding: '0 3px', minHeight: '20px' }}
            >
              <Box
                sx={{
                  width: '100%',
                  padding: '0 5px',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  maxWidth: '100%',
                }}
              >
                <Typography variant='body2' noWrap>
                  {item.province.slot} {item.province.name}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ color: colors[race.substring(0, 2)] }}
                >
                  {race}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 1 }}>
              <Box
                sx={{
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <TextField
                  name={`notes_${itemInd}`}
                  id={`notes_${itemInd}`}
                  variant='outlined'
                  placeholder='add notes to Intel'
                  value={item.content}
                  fullWidth
                  minRows={1}
                  size='small'
                  onChange={(e) => handleItemChange(e, colInd, itemInd)}
                />
              </Box>
              {item.province.requiredOps?.length > 0 && (
                <Box mt={1}>
                  <Typography
                    variant='caption'
                    style={{ marginTop: '5px' }}
                    textAlign='center'
                    component='div'
                  >
                    Province Ops:
                  </Typography>
                  <Stack
                    direction='row'
                    sx={{
                      maxWidth: '249px',
                      flexWrap: 'wrap',
                    }}
                  >
                    {item.province.requiredOps?.map((op, i: number) => {
                      const effect: Effect = effects[op];
                      const numb = i + 1;
                      const isRowEnd = numb % 4 === 0;
                      const deleteFunction = !column.requiredOps?.includes(op)
                        ? () => deleteOp(op, item)
                        : undefined;
                      return (
                        <Tooltip
                          arrow
                          placement='top'
                          title={
                            deleteFunction
                              ? `Delete ${op} from Province`
                              : `${op} a required OP on ${column.title} Group. To delete, remove the OP from the group below and add to individual provinces instead.`
                          }
                          key={effect.displayName}
                        >
                          <Chip
                            label={effect.shortName}
                            variant='outlined'
                            onDelete={deleteFunction}
                            size='small'
                            sx={{
                              marginTop: '5px',
                              marginRight: isRowEnd ? 0 : '3px',
                              width: '60px',
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Stack>
                </Box>
              )}
              {item.province.requiredObjectives?.length > 0 && (
                <Box>
                  <Typography
                    variant='caption'
                    style={{ marginTop: '5px' }}
                    textAlign='center'
                    component='div'
                  >
                    Province Objectives:
                  </Typography>
                  <Stack
                    direction='row'
                    sx={{
                      maxWidth: '249px',
                      flexWrap: 'wrap',
                    }}
                  >
                    {item.province.requiredObjectives?.map((ob: Objective) => (
                      <Tooltip
                        arrow
                        placement='top'
                        title={`Delete the ${ob.type} Objective from Province`}
                        key={ob.type}
                      >
                        <Chip
                          label={`${ob.type}: ${ob.value}`}
                          variant='outlined'
                          onDelete={() => deleteObjective(ob)}
                          size='small'
                          sx={{
                            marginTop: '5px',
                            marginRight: '3px',
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              )}
              {isWarPlan && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 1,
                  }}
                >
                  <Button
                    color='primary'
                    aria-label='add objective'
                    onClick={toggleDrawer}
                    variant='outlined'
                    size='small'
                    sx={{ mt: 1 }}
                  >
                    Add Orders
                  </Button>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Paper>
      )}
    </Draggable>
  );
};

export default Item;
